/**
 * Smart Pairing Recommendations Resolver for Velpula Mess Web Storefront
 */

const CATEGORY_COMPLEMENT_MAP = {
  biryani: {
    categories: ["starters", "tandoori kebabs", "tea", "desserts", "beverages", "currys"],
    keywords: ["salan", "raita", "meetha", "chai", "65", "tikka", "coke", "thums up", "gulab jamun"],
  },
  starters: {
    categories: ["biryani", "fried rice", "tea", "beverages", "rotis", "shawarma"],
    keywords: ["biryani", "chai", "naan", "rumali", "coke", "lemon tea"],
  },
  "tandoori kebabs": {
    categories: ["biryani", "rotis", "currys", "beverages", "tea"],
    keywords: ["rumali", "naan", "biryani", "butter chicken", "chai"],
  },
  currys: {
    categories: ["rotis", "biryani", "fried rice", "beverages", "starters"],
    keywords: ["naan", "roti", "paratha", "biryani", "jeera rice"],
  },
  rotis: {
    categories: ["currys", "tandoori kebabs", "starters", "beverages"],
    keywords: ["butter chicken", "paneer butter", "kadai", "salan"],
  },
  tea: {
    categories: ["starters", "desserts", "bakery"],
    keywords: ["osmania", "biscuit", "bun maska", "samosa", "puff", "meetha", "badam"],
  },
  shawarma: {
    categories: ["tea", "beverages", "starters", "fried rice"],
    keywords: ["chai", "coke", "mojito", "wings", "fries"],
  },
  "fried rice": {
    categories: ["starters", "currys", "beverages", "tea"],
    keywords: ["manchurian", "chilli chicken", "65", "chai", "soup"],
  },
  noodles: {
    categories: ["starters", "currys", "beverages", "soups"],
    keywords: ["manchurian", "chilli chicken", "soup", "chai"],
  },
  soups: {
    categories: ["starters", "fried rice", "noodles"],
    keywords: ["manchurian", "spring roll", "fried rice"],
  },
  "sea food": {
    categories: ["biryani", "rotis", "beverages", "starters"],
    keywords: ["rice", "biryani", "naan", "lemon"],
  },
};

export function getDishPairings(item, allItems = [], maxItems = 6) {
  if (!item || !Array.isArray(allItems) || allItems.length === 0) return [];

  const targetId = item.id || item._id;
  const result = [];
  const addedIds = new Set([targetId]);

  // 1. Explicit admin configured pairings
  if (Array.isArray(item.suggested_items) && item.suggested_items.length > 0) {
    for (const sId of item.suggested_items) {
      const match = allItems.find((it) => (it.id === sId || it._id === sId) && it.available !== false);
      if (match && !addedIds.has(match.id || match._id)) {
        result.push(match);
        addedIds.add(match.id || match._id);
        if (result.length >= maxItems) return result;
      }
    }
  }

  // 2. Intelligent Category / Keyword Complements
  const catKey = (item.category || "").toLowerCase().trim();
  const rule = CATEGORY_COMPLEMENT_MAP[catKey] || {
    categories: ["tea", "starters", "biryani", "desserts", "beverages"],
    keywords: ["chai", "65", "biryani", "meetha", "salan", "osmania"],
  };

  // 2a. Priority to keyword-matching items
  for (const kw of rule.keywords) {
    const matches = allItems.filter((it) => {
      const id = it.id || it._id;
      if (addedIds.has(id) || it.available === false) return false;
      return it.name.toLowerCase().includes(kw);
    });

    for (const m of matches) {
      result.push(m);
      addedIds.add(m.id || m._id);
      if (result.length >= maxItems) return result;
    }
  }

  // 2b. Priority to complement category items
  for (const compCat of rule.categories) {
    const matches = allItems.filter((it) => {
      const id = it.id || it._id;
      if (addedIds.has(id) || it.available === false) return false;
      return (it.category || "").toLowerCase().includes(compCat);
    });

    for (const m of matches) {
      result.push(m);
      addedIds.add(m.id || m._id);
      if (result.length >= maxItems) return result;
    }
  }

  // 2c. Fallback to general popular items
  if (result.length < maxItems) {
    for (const it of allItems) {
      const id = it.id || it._id;
      if (!addedIds.has(id) && it.available !== false) {
        result.push(it);
        addedIds.add(id);
        if (result.length >= maxItems) break;
      }
    }
  }

  return result;
}

export function getCartPairings(cartLines = [], allItems = [], maxItems = 6) {
  if (!Array.isArray(cartLines) || cartLines.length === 0 || !Array.isArray(allItems) || allItems.length === 0) {
    return allItems.filter((it) => it.available !== false).slice(0, maxItems);
  }

  const inCartIds = new Set(
    cartLines.map((l) => l.base_item_id || l.item_id || l.id || l._id)
  );

  const result = [];
  const addedIds = new Set([...inCartIds]);

  // 1. Gather all explicit suggestions from cart items
  for (const line of cartLines) {
    const rawId = line.base_item_id || line.item_id || line.id || line._id;
    const fullItem = allItems.find((it) => (it.id === rawId || it._id === rawId));
    if (fullItem && Array.isArray(fullItem.suggested_items)) {
      for (const sId of fullItem.suggested_items) {
        const match = allItems.find((it) => (it.id === sId || it._id === sId) && it.available !== false);
        if (match && !addedIds.has(match.id || match._id)) {
          result.push(match);
          addedIds.add(match.id || match._id);
          if (result.length >= maxItems) return result;
        }
      }
    }
  }

  // 2. Derive complement pairings for each cart item
  for (const line of cartLines) {
    const rawId = line.base_item_id || line.item_id || line.id || line._id;
    const fullItem = allItems.find((it) => (it.id === rawId || it._id === rawId)) || line;
    const itemPairings = getDishPairings(fullItem, allItems, 4);

    for (const p of itemPairings) {
      const pId = p.id || p._id;
      if (!addedIds.has(pId) && p.available !== false) {
        result.push(p);
        addedIds.add(pId);
        if (result.length >= maxItems) return result;
      }
    }
  }

  // 3. Fallback to popular add-ons
  const popularKeywords = ["chai", "osmania", "meetha", "salan", "raita", "thums", "coke", "65"];
  for (const kw of popularKeywords) {
    const matches = allItems.filter((it) => {
      const id = it.id || it._id;
      return !addedIds.has(id) && it.available !== false && it.name.toLowerCase().includes(kw);
    });
    for (const m of matches) {
      result.push(m);
      addedIds.add(m.id || m._id);
      if (result.length >= maxItems) return result;
    }
  }

  return result;
}
